import React, { useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Link } from "react-router-dom";
import {
  removeCartItem,
  updateCartItemQuantity,
} from "../actions/cart";
import { resolveMediaUrl } from "../helpers/api";
import { getPrintPreview } from "../helpers/printPreviews";

const CartPage = () => {
  const dispatch = useDispatch();
  const {
    items = [],
    total_display,
    total_cents,
    loading,
    error,
    status,
  } = useSelector((state) => state.cart);

  const hasItems = Array.isArray(items) && items.length > 0;
  const isSubmitted = status === "submitted";

  const subtotalLabel = useMemo(() => {
    if (total_cents && total_cents > 0) {
      return total_display || `$${(total_cents / 100).toFixed(2)}`;
    }
    return total_display || "Custom quote";
  }, [total_cents, total_display]);

  const handleDecrease = (itemId, currentQty) => {
    const next = Math.max(0, currentQty - 1);
    if (next === 0) {
      dispatch(removeCartItem(itemId));
    } else {
      dispatch(updateCartItemQuantity(itemId, next));
    }
  };

  const handleIncrease = (itemId, currentQty) => {
    dispatch(updateCartItemQuantity(itemId, currentQty + 1));
  };

  const handleRemove = (itemId) => {
    dispatch(removeCartItem(itemId));
  };

  return (
    <div className="space-y-8">
      <header className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="text-3xl font-bold text-gray-900 dark:text-white">Your Cart</h1>
          <p className="text-sm text-gray-600 dark:text-gray-300">
            Review your selections and proceed to checkout when you are ready.
          </p>
        </div>
        <Link
          to="/store"
          className="inline-flex items-center justify-center rounded-lg border border-gray-300 px-4 py-2 text-sm font-semibold text-gray-700 hover:bg-gray-100 dark:border-gray-600 dark:text-gray-200 dark:hover:bg-gray-700"
        >
          Continue shopping
        </Link>
      </header>

      {error && (
        <div className="rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700 dark:border-red-500/60 dark:bg-red-900/40 dark:text-red-200">
          {error}
        </div>
      )}

      {isSubmitted && (
        <div className="rounded-lg border border-green-200 bg-green-50 px-4 py-4 text-sm text-green-800 dark:border-green-500/40 dark:bg-green-900/30 dark:text-green-200">
          Thank you! Your order request has been submitted. We will be in touch shortly to confirm details.
        </div>
      )}

      {loading && !hasItems && (
        <div className="rounded-lg border border-gray-200 bg-gray-50 px-4 py-6 text-sm text-gray-600 dark:border-gray-700 dark:bg-gray-800/60 dark:text-gray-300">
          Loading your cart...
        </div>
      )}

      {!hasItems && !loading ? (
        <div className="rounded-xl border border-dashed border-gray-300 bg-white px-6 py-16 text-center dark:border-gray-700 dark:bg-gray-800/50">
          <h2 className="text-xl font-semibold text-gray-800 dark:text-gray-100 mb-2">
            Your cart is empty
          </h2>
          <p className="text-sm text-gray-600 dark:text-gray-300 mb-4">
            Browse the store and add services or products to build your order.
          </p>
          <Link
            to="/store"
            className="inline-flex items-center justify-center rounded-lg bg-blue-600 px-4 py-2 text-sm font-semibold text-white hover:bg-blue-700"
          >
            Explore store
          </Link>
        </div>
      ) : null}

      {hasItems && (
        <div className="space-y-6">
          <div className="overflow-hidden rounded-xl border border-gray-200 bg-white shadow-sm dark:border-gray-700 dark:bg-gray-800">
            <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
              <thead className="bg-gray-50 dark:bg-gray-900/60">
                <tr>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-300">
                    Item
                  </th>
                  <th scope="col" className="px-6 py-3 text-center text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-300">
                    Quantity
                  </th>
                  <th scope="col" className="px-6 py-3 text-right text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-300">
                    Price
                  </th>
                  <th scope="col" className="px-6 py-3 text-right text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-300">
                    Total
                  </th>
                  <th scope="col" className="px-6 py-3" aria-hidden="true"></th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100 dark:divide-gray-700">
                {items.map((item) => {
                  const product = item.product || {};
                  const printRequest = item.print_request || null;
                  const laserRequest = item.laser_request || null;
                  const displayName =
                    item.display_name ||
                    product.name ||
                    (printRequest ? `3D Print Quote #${printRequest.id}` : laserRequest ? `${laserRequest.process === "plasma" ? "Plasma" : "Laser"} Cutting Quote #${laserRequest.id}` : "Item");
                  const previewUrl = printRequest ? getPrintPreview(printRequest.id) : "";
                  const imageUrl = previewUrl || (product.image ? resolveMediaUrl(product.image) : "");
                  const detailLines = [];

                  if (printRequest) {
                    if (printRequest.filename) {
                      detailLines.push(`File: ${printRequest.filename}`);
                    }
                    if (printRequest.color) {
                      detailLines.push(`Color: ${printRequest.color}`);
                    }
                    if (printRequest.material) {
                      detailLines.push(`Material: ${printRequest.material}`);
                    }
                    if (typeof printRequest.infill_percent === "number") {
                      detailLines.push(`Infill: ${printRequest.infill_percent}%`);
                    }
                    if (printRequest.quality) {
                      detailLines.push(`Quality: ${printRequest.quality}`);
                    }
                    if (printRequest.express) {
                      detailLines.push("Express production");
                    }
                  } else if (laserRequest) {
                    detailLines.push(`File: ${laserRequest.filename}`);
                    detailLines.push(`${laserRequest.material}, ${laserRequest.thickness_mm} mm`);
                    detailLines.push(`${laserRequest.width_mm} × ${laserRequest.height_mm} mm · ${laserRequest.quantity} part${laserRequest.quantity === 1 ? "" : "s"}`);
                  } else if (product.description) {
                    detailLines.push(product.description);
                  }

                  return (
                    <tr key={item.id} className="bg-white dark:bg-gray-800">
                      <td className="px-6 py-4">
                        <div className="flex items-center gap-4">
                          <div className="h-16 w-16 overflow-hidden rounded-lg bg-gray-100 dark:bg-gray-700">
                            {imageUrl ? (
                              <img
                                src={imageUrl}
                                alt={product.name || displayName}
                                className="h-full w-full object-cover"
                              />
                            ) : (
                              <div className="flex h-full w-full items-center justify-center text-xs text-gray-400">
                                No image
                              </div>
                            )}
                          </div>
                          <div>
                            <p className="font-semibold text-gray-900 dark:text-gray-100">
                              {displayName}
                            </p>
                            {product.category?.name && (
                              <p className="text-xs text-gray-500 dark:text-gray-400">
                                {product.category.name}
                              </p>
                            )}
                            {detailLines.map((line) => (
                              <p key={line} className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                                {line}
                              </p>
                            ))}
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-4 text-center">
                        {printRequest || laserRequest || product.is_digital ? (
                          <div>
                            <span className="font-semibold text-gray-900 dark:text-gray-100">{item.quantity}</span>
                            <span className="mt-1 block text-[10px] font-bold uppercase tracking-wide text-gray-400">
                              {product.is_digital ? "Digital" : "Quoted"}
                            </span>
                          </div>
                        ) : (
                          <div className="inline-flex items-center rounded-full border border-gray-300 bg-white text-sm dark:border-gray-600 dark:bg-gray-900">
                            <button
                              type="button"
                              onClick={() => handleDecrease(item.id, item.quantity)}
                              className="px-3 py-1 text-gray-700 hover:bg-gray-100 dark:text-gray-200 dark:hover:bg-gray-700"
                              aria-label="Decrease quantity"
                            >
                              −
                            </button>
                            <span className="px-4 text-gray-900 dark:text-gray-100">
                              {item.quantity}
                            </span>
                            <button
                              type="button"
                              onClick={() => handleIncrease(item.id, item.quantity)}
                              className="px-3 py-1 text-gray-700 hover:bg-gray-100 dark:text-gray-200 dark:hover:bg-gray-700"
                              aria-label="Increase quantity"
                            >
                              +
                            </button>
                          </div>
                        )}
                      </td>
                      <td className="px-6 py-4 text-right text-sm text-gray-700 dark:text-gray-200">
                        {item.unit_price_display || "Custom"}
                      </td>
                      <td className="px-6 py-4 text-right text-sm font-semibold text-gray-900 dark:text-gray-100">
                        {item.line_total_display || "-"}
                      </td>
                      <td className="px-6 py-4 text-right">
                        <button
                          type="button"
                          onClick={() => handleRemove(item.id)}
                          className="text-xs font-semibold text-red-600 hover:text-red-700 dark:text-red-400 dark:hover:text-red-300"
                        >
                          Remove
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          <div className="flex flex-col gap-4 rounded-xl border border-gray-200 bg-white p-6 shadow-sm dark:border-gray-700 dark:bg-gray-800">
            <div className="flex items-center justify-between text-sm">
              <span className="text-gray-600 dark:text-gray-300">Subtotal</span>
              <span className="text-base font-semibold text-gray-900 dark:text-gray-100">
                {subtotalLabel}
              </span>
            </div>
            <p className="text-xs text-gray-500 dark:text-gray-400">
              Final pricing may vary for custom or quoted items. We will confirm totals during checkout.
            </p>
            <div className="flex flex-col gap-2 sm:flex-row sm:justify-end">
              <Link
                to="/store"
                className="inline-flex items-center justify-center rounded-lg border border-gray-300 px-4 py-2 text-sm font-semibold text-gray-700 hover:bg-gray-100 dark:border-gray-600 dark:text-gray-200 dark:hover:bg-gray-700"
              >
                Add more items
              </Link>
              {hasItems && !isSubmitted ? (
                <Link
                  to="/checkout"
                  className="inline-flex items-center justify-center rounded-lg bg-blue-600 px-4 py-2 text-sm font-semibold text-white shadow-sm transition hover:bg-blue-700"
                >
                  Proceed to checkout
                </Link>
              ) : (
                <span className="inline-flex items-center justify-center rounded-lg bg-gray-300 px-4 py-2 text-sm font-semibold text-gray-500 dark:bg-gray-700 dark:text-gray-400">
                  Proceed to checkout
                </span>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default CartPage;
